import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { User } from 'firebase/auth';
import { authService } from '../services/auth';
import { userStorage, StoredUser } from '../services/userStorage';
import { headerService } from '../services/headers';
import { authApiService } from '../services/auth';
import { tokenRefreshService } from '../services/tokenRefresh';
import { storageService } from '../services/storage';
import { UserRole } from '../types';
import { notificationService } from '../services/notifications';

// Tipos
export interface AuthUser extends User {
  customClaims?: any;
  role?: UserRole;
  id?: string; // Añadir id para compatibilidad
}

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [storedUser, setStoredUser] = useState<StoredUser | null>(null);
  const listenerRef = useRef<(() => void) | null>(null);
  const hasRefreshedRef = useRef(false);
  const isRefreshingRef = useRef(false);

  // Memoizar el estado de autenticación
  const isAuthenticated = useMemo(() => !!user, [user]);

  useEffect(() => {
    // Evitar múltiples listeners
    if (listenerRef.current) {
      return;
    }

    const unsubscribe = authService.onAuthChange(async (firebaseUser) => {
      // Resetear las banderas cuando cambia el usuario
      hasRefreshedRef.current = false;
      isRefreshingRef.current = false;
      
      if (firebaseUser) {
        try {
          // Obtener el token para verificar claims personalizados
          const tokenResult = await firebaseUser.getIdTokenResult();
          const customClaims = tokenResult.claims as any;
          
          // Verificar si tenemos otsUser (disponible después de signup o en refresh)
          if (customClaims.otsUser) {
            // Asegurar que el usuario tenga el id (uid de Firebase)
            const userDataWithId = {
              ...customClaims.otsUser,
              id: firebaseUser.uid
            };
            await userStorage.saveUser(userDataWithId);
            const storedUserData = await userStorage.getUser();
            setStoredUser(storedUserData);
            
            // Usar el rol calculado del storage
            const role = userStorage.getUserRole();
            setUserRole(role);
            
            // Quitar console.log aquí

            // Crear usuario extendido con información de roles
            const authUser: AuthUser = {
              ...firebaseUser,
              customClaims,
              role: role || undefined,
              id: firebaseUser.uid // Establecer el id como el uid de Firebase
            };
            setUser(authUser);
          } else {
            // Intentar cargar usuario desde storage local (si ya existe)
            const existingUser = await userStorage.getUser();
            if (existingUser) {
              setStoredUser(existingUser);
              const role = userStorage.getUserRole();
              setUserRole(role);
              
              // Crear usuario extendido con información de roles
              const authUser: AuthUser = {
                ...firebaseUser,
                customClaims,
                role: role || undefined,
                id: firebaseUser.uid // Establecer el id como el uid de Firebase
              };
              setUser(authUser);
            } else {
              // Usuario nuevo sin otsUser, rol por defecto
              const role = {
                id: 'comensal',
                name: 'Cliente',
                permissions: ['read']
              };
              setUserRole(role);
              
              // Crear usuario extendido con información de roles
              const authUser: AuthUser = {
                ...firebaseUser,
                customClaims,
                role: role,
                id: firebaseUser.uid // Establecer el id como el uid de Firebase
              };
              setUser(authUser);
            }
          }
          
          // Actualizar token en el servicio de headers
          const idToken = await firebaseUser.getIdToken();
          headerService.setAuthToken(idToken);
          
          // Inicializar información del token para refresh inteligente
          tokenRefreshService.initializeTokenInfo(tokenResult);

          // Sincronizar/Enviar token FCM tras login
          try {
            await notificationService.syncFCMToken(firebaseUser.uid);
          } catch (e) {
            console.warn('⚠️ No se pudo sincronizar token FCM tras login:', e);
          }
          
        } catch (error) {
          console.error('❌ useAuth - Error obteniendo claims del usuario:', error);
          setUser({
            ...firebaseUser,
            id: firebaseUser.uid
          } as AuthUser);
          setUserRole({
            id: 'comensal',
            name: 'Cliente',
            permissions: ['read']
          });
        }
      } else {
        setUser(null);
        setUserRole(null);
        setStoredUser(null);
        
        // Limpiar TODOS los datos cuando el usuario se desmonta
        await storageService.clearAllAppData();
        headerService.clearAuthToken();
      }
      
      setIsLoading(false);
    });

    // Guardar referencia del listener
    listenerRef.current = unsubscribe;

    return () => {
      if (listenerRef.current) {
        listenerRef.current();
        listenerRef.current = null;
      }
    };
  }, []);

  // Efecto para refrescar automáticamente el token después del login
  // DESHABILITADO TEMPORALMENTE para evitar demasiadas peticiones
  // useEffect(() => {
  //   if (user && isAuthenticated && !isLoading && !hasRefreshedRef.current) {
  //     // Marcar que ya se refrescó para evitar bucles
  //     hasRefreshedRef.current = true;
  //     
  //     // Esperar un momento para asegurar que todo esté listo
  //     const timer = setTimeout(async () => {
  //       try {
  //         console.log('🔄 Refrescando token automáticamente después del login...');
  //         await refreshUserToken();
  //       } catch (error) {
  //         console.error('❌ Error refrescando token después del login:', error);
  //       }
  //     }, 2000); // Esperar 2 segundos

  //     return () => clearTimeout(timer);
  //   }
  // }, [isAuthenticated]); // Solo depender de isAuthenticated para evitar bucles

  // Memoizar las funciones de verificación de permisos
  const hasPermission = useCallback((permission: string): boolean => {
    return userStorage.hasPermission(permission);
  }, []);

  const isAdmin = useCallback((): boolean => {
    return userStorage.isAdmin();
  }, []);

  const canViewSalesReports = useCallback((): boolean => {
    return userStorage.canViewSalesReports();
  }, []);

  // Función para verificar si es propietario
  const isOwner = useCallback((): boolean => {
    return userStorage.isOwner();
  }, []);

  // Función para verificar si es gerente
  const isManager = useCallback((): boolean => {
    return userStorage.isManager();
  }, []);

  // Función para verificar si es manager o kitchen
  const isManagerOrKitchen = useCallback((): boolean => {
    return userStorage.isManagerOrKitchen();
  }, []);

  // Función para verificar si es cocinero
  const isKitchen = useCallback((): boolean => {
    return userStorage.isKitchen();
  }, []);

  // Función para verificar si es mesero
  const isWaiter = useCallback((): boolean => {
    return userStorage.isWaiter();
  }, []);

  // Función para cerrar sesión
  const signOut = async (): Promise<void> => {
    try {
      console.log('🔄 Iniciando proceso de logout completo...');
      const userIdForCleanup = user?.id;
      
      // Eliminar token FCM en backend antes de cerrar sesión
      try {
        if (userIdForCleanup) {
          await notificationService.clearFCMToken(userIdForCleanup);
        }
      } catch (e) {
        console.warn('⚠️ No se pudo limpiar token FCM en logout:', e);
      }
      
      // 1. Cerrar sesión de Firebase
      await authService.signOut();
      console.log('✅ Sesión de Firebase cerrada');
      
      // 2. Limpiar TODOS los datos de la aplicación
      await storageService.clearAllAppData();
      console.log('✅ Todos los datos de la aplicación limpiados');
      
      // 3. Limpiar token de headers
      headerService.clearAuthToken();
      console.log('✅ Token de headers limpiado');
      
      // 4. Debug para verificar limpieza
      storageService.debugStorage();
      
      console.log('🎉 Logout completo finalizado');
    } catch (error) {
      console.error('❌ Error cerrando sesión:', error);
      throw error;
    }
  };

  // Función para refrescar el token y claims del usuario
  const refreshUserToken = async (): Promise<void> => {
    if (!user) return;
    
    // Evitar múltiples refreshes simultáneos
    if (isRefreshingRef.current) {
      console.log('🔄 Refresh ya en progreso, saltando...');
      return;
    }
    
    isRefreshingRef.current = true;
    
    try {
      console.log('🔄 Refrescando token y claims del usuario...');
      
      // Usar el servicio centralizado de refresh
      const success = await authApiService.refreshToken();
      
      if (success) {
        // Actualizar estado local con los datos frescos
        const storedUserData = await userStorage.getUser();
        setStoredUser(storedUserData);
        
        // Usar el rol calculado del storage
        const role = userStorage.getUserRole();
        setUserRole(role);
        
        // Obtener el usuario actual de Firebase para actualizar claims
        const currentFirebaseUser = authService.auth.currentUser;
        if (currentFirebaseUser) {
          const tokenResult = await currentFirebaseUser.getIdTokenResult();
          const customClaims = tokenResult.claims as any;
          const updatedUser: AuthUser = {
            ...currentFirebaseUser,
            customClaims,
            role: role || undefined,
            id: currentFirebaseUser.uid // Asegurar que el id esté presente
          };
          setUser(updatedUser);

          // Re-sincronizar token FCM tras refresh de sesión
          try {
            await notificationService.syncFCMToken(currentFirebaseUser.uid);
          } catch (e) {
            console.warn('⚠️ No se pudo re-sincronizar token FCM tras refresh:', e);
          }
        }
        
        console.log('✅ Token y claims actualizados correctamente');
      } else {
        console.warn('⚠️ No se pudo refrescar el token');
      }
      
    } catch (error) {
      console.error('❌ Error refrescando token:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  };

  // Función para forzar la actualización del rol (útil después de cambios en el backend)
  const forceRoleUpdate = async (): Promise<void> => {
    if (!user) return;
    
    try {
      // Quitar console.log aquí
      await refreshUserToken();
      // Quitar console.log aquí
    } catch (error) {
      console.error('❌ Error forzando actualización del rol:', error);
    }
  };

  // Función para forzar actualización completa después del login
  const forceCompleteUpdate = async (): Promise<void> => {
    if (!user) return;
    
    try {
      // Quitar console.log aquí
      
      // Esperar un momento para asegurar que el backend haya procesado todo
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refrescar token y claims
      await refreshUserToken();
      
      // Forzar actualización del rol
      const role = userStorage.getUserRole();
      setUserRole(role);
      
      // Actualizar storedUser
      const storedUserData = await userStorage.getUser();
      setStoredUser(storedUserData);
      
      // Quitar console.log aquí
      
    } catch (error) {
      console.error('❌ Error en actualización completa:', error);
    }
  };

  // Función para asegurar que el token sea válido
  const ensureValidToken = async (): Promise<boolean> => {
    if (!user) return false;
    
    try {
      // Obtener el usuario actual de Firebase para verificar el token
      const currentFirebaseUser = authService.auth.currentUser;
      if (!currentFirebaseUser) {
        return false;
      }
      
      // Verificar si el token está próximo a expirar (menos de 5 minutos)
      const tokenResult = await currentFirebaseUser.getIdTokenResult();
      const expirationTime = tokenResult.expirationTime;
      const currentTime = new Date();
      const timeUntilExpiration = new Date(expirationTime).getTime() - currentTime.getTime();
      const fiveMinutes = 5 * 60 * 1000; // 5 minutos en milisegundos
      
      if (timeUntilExpiration < fiveMinutes) {
        // Quitar console.log aquí
        await refreshUserToken();
      }
      
      return true;
    } catch (error) {
      console.error('❌ Error verificando token:', error);
      return false;
    }
  };

  return {
    user,
    isLoading,
    isAuthenticated,
    userRole,
    storedUser,
    hasPermission,
    isAdmin,
    canViewSalesReports,
    isOwner,
    isManager,
    isManagerOrKitchen,
    isKitchen,
    isWaiter,
    signOut,
    refreshUserToken,
    forceRoleUpdate,
    forceCompleteUpdate,
    ensureValidToken,
  };
}; 